import { builtinImportAliases } from "./aliases.js";
import { aliasKeyOf, headerKey, learnableAliasKey } from "./header-key.js";
import { findImportTargetField, importTargetFields } from "./target-fields.js";
import { stringsSimilarity } from "./third-party/tableflow-similarity.js";
import { IMPORT_SIMILARITY_THRESHOLD, } from "./types.js";
const none = (columnIndex) => Object.freeze({
    columnIndex,
    decision: "UNMAPPED",
    targetField: null,
    confidence: "NONE",
    suggestionSource: "NONE",
    suggestedTargetField: null,
    origin: "SUGGESTION",
    remember: false,
});
const high = (columnIndex, match) => Object.freeze({
    columnIndex,
    decision: "MAPPED",
    targetField: match.targetField,
    confidence: "HIGH",
    suggestionSource: match.source,
    suggestedTargetField: match.targetField,
    origin: "SUGGESTION",
    remember: false,
});
const medium = (columnIndex, match) => Object.freeze({
    columnIndex,
    decision: "UNMAPPED",
    targetField: null,
    confidence: "MEDIUM",
    suggestionSource: match.source,
    suggestedTargetField: match.targetField,
    origin: "SUGGESTION",
    remember: false,
});
/** A target key without its underscores is a header key: `location_city` is `locationcity`. */
const targetKeyOf = (key) => key.replace(/_/g, "");
function buildLookups(learnedAliases) {
    const learned = new Map();
    for (const alias of [...learnedAliases].sort((a, b) => a.registryVersion - b.registryVersion)) {
        // An alias that names a field this version does not know is ignored.
        if (findImportTargetField(alias.targetField) === undefined)
            continue;
        if (!learned.has(alias.key))
            learned.set(alias.key, alias.targetField);
    }
    const builtin = new Map(builtinImportAliases.map((alias) => [
        alias.key,
        { targetField: alias.targetField, ordinal: alias.ordinal },
    ]));
    const targetKeys = new Map(importTargetFields.map((target) => [targetKeyOf(target.key), target.key]));
    const similarityPool = [
        ...[...learned].map(([key, targetField]) => ({ key, targetField })),
        ...builtinImportAliases.map((alias) => ({ key: alias.key, targetField: alias.targetField })),
        ...importTargetFields.map((target) => ({
            key: targetKeyOf(target.key),
            targetField: target.key,
        })),
    ];
    return { learned, builtin, targetKeys, similarityPool };
}
function exactMatch(header, lookups) {
    const whole = aliasKeyOf(header);
    if (whole === "")
        return null;
    const learned = lookups.learned.get(whole);
    if (learned !== undefined)
        return { targetField: learned, source: "LEARNED" };
    const builtin = lookups.builtin.get(whole);
    if (builtin !== undefined)
        return { targetField: builtin.targetField, source: "BUILT_IN" };
    const target = lookups.targetKeys.get(whole);
    if (target !== undefined)
        return { targetField: target, source: "BUILT_IN" };
    // `Celebrity3`: a number after the name counts the columns of a field that takes several.
    const { key, ordinal } = headerKey(header);
    if (ordinal !== null) {
        const alias = lookups.builtin.get(key);
        if (alias?.ordinal === true)
            return { targetField: alias.targetField, source: "BUILT_IN" };
        const ownKey = lookups.targetKeys.get(key);
        if (ownKey !== undefined && findImportTargetField(ownKey)?.multiple === true) {
            return { targetField: ownKey, source: "BUILT_IN" };
        }
    }
    return null;
}
function similarMatch(header, lookups) {
    const key = aliasKeyOf(header);
    if (key === "")
        return null;
    let best = null;
    let bestScore = 0;
    for (const candidate of lookups.similarityPool) {
        const score = stringsSimilarity(key, candidate.key);
        if (score >= IMPORT_SIMILARITY_THRESHOLD && score > bestScore) {
            best = { targetField: candidate.targetField, source: "SIMILARITY" };
            bestScore = score;
        }
    }
    return best;
}
const isSingleValue = (targetField) => findImportTargetField(targetField)?.multiple !== true;
/**
 * One decision per header. HIGH matches come `MAPPED`; MEDIUM matches and no match come `UNMAPPED`.
 * If more than one column is HIGH for the same single-value field, every one of them is lowered to
 * MEDIUM: staff choose which column fills it.
 */
export function suggestColumns(input) {
    const lookups = buildLookups(input.learnedAliases);
    const raw = input.headers.map((header, columnIndex) => {
        const exact = exactMatch(header, lookups);
        if (exact !== null)
            return { columnIndex, match: exact, confidence: "HIGH" };
        const similar = similarMatch(header, lookups);
        if (similar !== null)
            return { columnIndex, match: similar, confidence: "MEDIUM" };
        return { columnIndex, match: null, confidence: "NONE" };
    });
    const claims = new Map();
    for (const entry of raw) {
        if (entry.confidence === "HIGH" &&
            entry.match !== null &&
            isSingleValue(entry.match.targetField)) {
            claims.set(entry.match.targetField, (claims.get(entry.match.targetField) ?? 0) + 1);
        }
    }
    return Object.freeze(raw.map((entry) => {
        if (entry.match === null)
            return none(entry.columnIndex);
        if (entry.confidence === "MEDIUM")
            return medium(entry.columnIndex, entry.match);
        const contested = isSingleValue(entry.match.targetField) && (claims.get(entry.match.targetField) ?? 0) > 1;
        return contested
            ? medium(entry.columnIndex, entry.match)
            : high(entry.columnIndex, entry.match);
    }));
}
/**
 * Turns what staff sent into saved decisions. The server (and the admin's preview) work out the rest:
 * the confidence and source come from the suggestion for the column; the origin is `SUGGESTION` when
 * the decision is exactly what the suggester produced and `STAFF` when staff changed it (a MEDIUM
 * suggestion that staff accept is a staff decision); `remember` is kept only for a staff decision
 * that maps a column whose header can be learned. Columns come out in index order. An input for a
 * column the suggestions do not have is dropped (the validator reports it first).
 */
export function resolveDecisions(input) {
    const resolved = [];
    for (const decision of input.decisions) {
        const suggestion = input.suggestions[decision.columnIndex];
        if (suggestion === undefined)
            continue;
        const targetField = decision.decision === "MAPPED" ? (decision.targetField ?? null) : null;
        const untouched = decision.decision === suggestion.decision && targetField === suggestion.targetField;
        const origin = untouched ? "SUGGESTION" : "STAFF";
        const header = input.headers[decision.columnIndex] ?? "";
        resolved.push(Object.freeze({
            columnIndex: decision.columnIndex,
            decision: decision.decision,
            targetField,
            confidence: suggestion.confidence,
            suggestionSource: suggestion.suggestionSource,
            suggestedTargetField: suggestion.suggestedTargetField,
            origin,
            remember: origin === "STAFF" &&
                decision.decision === "MAPPED" &&
                decision.remember === true &&
                learnableAliasKey(header) !== null,
        }));
    }
    return Object.freeze(resolved.sort((a, b) => a.columnIndex - b.columnIndex));
}
/**
 * "Re-run suggestions": applies the current registry to the columns that are still UNMAPPED. A column
 * that is mapped or ignored is never changed. A new HIGH match for a single-value field that another
 * column already fills is lowered to MEDIUM.
 */
export function rerunSuggestions(input) {
    const fresh = suggestColumns({ headers: input.headers, learnedAliases: input.learnedAliases });
    const taken = new Set(input.current
        .filter((decision) => decision.decision === "MAPPED" && decision.targetField !== null)
        .map((decision) => decision.targetField));
    return Object.freeze(input.current.map((decision) => {
        if (decision.decision !== "UNMAPPED")
            return decision;
        const suggestion = fresh[decision.columnIndex];
        if (suggestion === undefined)
            return decision;
        if (suggestion.decision === "MAPPED" &&
            suggestion.targetField !== null &&
            isSingleValue(suggestion.targetField) &&
            taken.has(suggestion.targetField)) {
            return medium(decision.columnIndex, {
                targetField: suggestion.targetField,
                source: suggestion.suggestionSource,
            });
        }
        if (suggestion.decision === "MAPPED" && suggestion.targetField !== null) {
            taken.add(suggestion.targetField);
        }
        return suggestion;
    }));
}
